import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Cloud, CloudRain, CloudSnow, Sun, CloudDrizzle, CloudLightning, CloudSun, CloudFog } from "lucide-react";

// WMO weather codes → icon + label
function iconFor(code) {
  if (code == null) return { Icon: Cloud, label: "—" };
  if (code === 0) return { Icon: Sun, label: "Clear" };
  if ([1,2].includes(code)) return { Icon: CloudSun, label: "Mostly sunny" };
  if (code === 3) return { Icon: Cloud, label: "Overcast" };
  if ([45,48].includes(code)) return { Icon: CloudFog, label: "Fog" };
  if ([51,53,55,56,57].includes(code)) return { Icon: CloudDrizzle, label: "Drizzle" };
  if ([61,63,65,66,67,80,81,82].includes(code)) return { Icon: CloudRain, label: "Rain" };
  if ([71,73,75,77,85,86].includes(code)) return { Icon: CloudSnow, label: "Snow" };
  if ([95,96,99].includes(code)) return { Icon: CloudLightning, label: "Storm" };
  return { Icon: Cloud, label: "—" };
}

const DAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function WeatherStrip({ destination, start }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!destination) return;
    setData(null); setErr(false);
    const params = new URLSearchParams({ destination, days: "7" });
    if (start) params.append("start", start);
    api.get(`/weather?${params.toString()}`)
      .then(r => setData(r.data))
      .catch(() => setErr(true));
  }, [destination, start]);

  if (err) return null;
  if (!data) {
    return (
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="min-w-[72px] h-[86px] rounded-lg bg-white/10 border border-white/15 animate-pulse"/>
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="weather-strip">
      {data.daily.map((d, i) => {
        const { Icon, label } = iconFor(d.weather_code);
        const dt = new Date(d.date + "T00:00:00");
        const dayName = i === 0 ? "Today" : DAY_NAMES[dt.getDay()];
        return (
          <div key={d.date} className="min-w-[74px] px-3 py-2 rounded-lg bg-white/12 backdrop-blur-sm border border-white/20 text-center text-white" title={label} data-testid={`weather-day-${i}`}>
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-white/70">{dayName}</div>
            <div className="my-1 flex justify-center"><Icon size={20} strokeWidth={1.8}/></div>
            <div className="font-mono-code text-xs font-semibold">{Math.round(d.temp_max_c)}° <span className="text-white/60">{Math.round(d.temp_min_c)}°</span></div>
            {d.precip_prob != null && d.precip_prob > 20 && (
              <div className="font-mono-code text-[9px] text-sky-200 mt-0.5">{d.precip_prob}%</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
